/**
 * Connectors hooks — managed-MCP-gateway connections + toolkit catalog.
 *
 * Reads:
 *  - useToolkitCatalog: catalog of toolkits the gateway exposes
 *  - useConnections: this user's connected toolkits (per workspace)
 *
 * Mutations:
 *  - useStartConnect: kick off the OAuth dance (POST /start) in a popup
 *    window so the user is never navigated away from the current view
 *    (settings or onboarding). Falls back to a full-page redirect only
 *    when the popup is blocked.
 *  - useDisconnectConnection: revoke a connection (Bower + gateway)
 *
 * ## Why a popup, not a full-page redirect
 *
 * The gateway (Composio) hosts the OAuth consent flow on its own domain.
 * Redirecting the whole tab there means a flaky third-party consent step
 * can strand the user away from Bower — unacceptable mid-onboarding. A
 * popup keeps the app mounted underneath; the popup returns to Bower's
 * `/connectors/callback` route, which `postMessage`s the outcome back to
 * this opener and closes itself. (An iframe can't be used — the gateway
 * sets `frame-ancestors 'none'`, which blocks framing but NOT popups.)
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConnectionSummary, ToolkitCatalogEntry } from '@bower/shared';
import { connectorsService } from '../../services/connectors.service';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../ui/useAppToast';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import {
  CONNECTOR_OAUTH_MESSAGE_TYPE,
  type ConnectorOAuthMessage,
} from '../../constants/connectorOAuth';

export const toolkitCatalogKey = ['connectorToolkitCatalog'] as const;
export const connectionsKey = (workspaceId: string | null) => ['connections', workspaceId] as const;

export type OAuthOutcome = 'success' | 'failed' | 'cancelled';

/** Abandoned-popup cap for waitForConnectorOAuth (see comment there). */
const OAUTH_MAX_WAIT_MS = 10 * 60 * 1000;
const OAUTH_RECONCILE_INITIAL_DELAY_MS = 1_000;
const OAUTH_RECONCILE_MAX_DELAY_MS = 15_000;
const OAUTH_FAILED_CALLBACK_GRACE_MS = 20_000;

/**
 * Opens the OAuth popup SYNCHRONOUSLY. Must be called directly inside the
 * click handler (before any `await`) or the browser's popup blocker fires —
 * a popup opened after an async hop isn't tied to the user gesture. The
 * caller then points the returned window at the gateway redirect URL once
 * `POST /start` resolves.
 *
 * Returns `null` when the popup is blocked — callers fall back to a
 * full-page redirect.
 */
export function openConnectorOAuthPopup(): Window | null {
  const width = 520;
  const height = 680;
  // Centre over the current window, dual-monitor aware.
  const dualLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualTop = window.screenTop ?? window.screenY ?? 0;
  const outerW = window.outerWidth || document.documentElement.clientWidth || width;
  const outerH = window.outerHeight || document.documentElement.clientHeight || height;
  const left = dualLeft + Math.max(0, (outerW - width) / 2);
  const top = dualTop + Math.max(0, (outerH - height) / 2);

  return window.open(
    '',
    'bower-connector-oauth',
    `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`,
  );
}

/**
 * Resolves once the OAuth popup reports its outcome (via postMessage from
 * `/connectors/callback`), Bower reconciles the connection independently, or
 * the user closes the popup ('cancelled').
 * Wrong-origin / wrong-shape messages are ignored so a hostile tab can't
 * spoof a success.
 */
function waitForConnectorOAuth(
  popup: Window,
  reconcile: () => Promise<OAuthOutcome | null>,
  /** Settles the flow as 'cancelled' and clears every timer/listener. Wired to
   * the owning hook's unmount: without it the self-rescheduling reconcile loop
   * outlives the page — zombie polling in prod, and in tests a leaked timer
   * that fires inside the NEXT test (a phantom listConnections call) or after
   * teardown (`window is not defined`). */
  abortSignal?: AbortSignal,
): Promise<OAuthOutcome> {
  return new Promise<OAuthOutcome>((resolve) => {
    let settled = false;
    let failedCallbackReceived = false;
    let failedCallbackDeadlineMs: number | undefined;
    let reconcileDelayMs = OAUTH_RECONCILE_INITIAL_DELAY_MS;
    let reconcileTimerId: number | undefined;

    const onAbort = () => finish('cancelled');

    const finish = (outcome: OAuthOutcome) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(pollId);
      window.clearTimeout(maxWaitId);
      if (reconcileTimerId !== undefined) window.clearTimeout(reconcileTimerId);
      // Remove this flow's abort listener from the SHARED unmount controller —
      // {once:true} only removes it after it fires, so without this every
      // settled attempt in a session leaks a closure (popup Window, reconcile
      // args, resolver) on the controller until unmount (CR-032).
      abortSignal?.removeEventListener('abort', onAbort);
      resolve(outcome);
    };

    const closePopup = () => {
      try {
        popup.close();
      } catch {
        // Popup may already be closing itself; ignore.
      }
    };

    // Self-healing cap: a popup that neither completes nor is closed (a
    // gateway error page left open, network partition) would otherwise leave
    // the button spinning and the listeners registered for the page's life.
    // Treated as 'cancelled' — silent, but still refetches connections in
    // case auth actually completed.
    const maxWaitId = window.setTimeout(() => finish('cancelled'), OAUTH_MAX_WAIT_MS);

    const onMessage = (event: MessageEvent) => {
      // Only trust messages from our own origin and our own envelope.
      if (event.origin !== window.location.origin) return;
      const data = event.data as Partial<ConnectorOAuthMessage> | null;
      if (!data || data.type !== CONNECTOR_OAUTH_MESSAGE_TYPE) return;
      if (data.status === 'success') {
        finish('success');
        closePopup();
        return;
      }

      // Composio's terminal redirect can report an invalid/expired state after
      // provider authorisation has already activated the account. Keep the
      // backend row authoritative for a short grace period instead of turning
      // that third-party redirect into an immediate false failure.
      failedCallbackReceived = true;
      failedCallbackDeadlineMs ??= Date.now() + OAUTH_FAILED_CALLBACK_GRACE_MS;
      closePopup();
    };

    // `window.closed` is one of the few cross-origin reads the browser
    // permits, so we can detect a user closing the popup mid-auth.
    const pollId = window.setInterval(() => {
      if (popup.closed && !failedCallbackReceived) finish('cancelled');
    }, 500);

    if (abortSignal) {
      if (abortSignal.aborted) {
        finish('cancelled');
        return;
      }
      abortSignal.addEventListener('abort', onAbort, { once: true });
    }

    const reconcileOnce = async () => {
      let outcome: OAuthOutcome | null = null;
      try {
        outcome = await reconcile();
      } catch {
        // The callback and later reconciliation attempts remain available
        // after a transient API/network failure.
      }
      if (settled) return;
      if (outcome) {
        finish(outcome);
        closePopup();
        return;
      }
      // Only the serialized reconciliation loop may enforce this deadline.
      // A standalone failure timer can otherwise settle while an earlier GET
      // is still in flight and discard its authoritative ACTIVE response.
      if (failedCallbackDeadlineMs !== undefined && Date.now() >= failedCallbackDeadlineMs) {
        finish('failed');
        return;
      }
      reconcileDelayMs = Math.min(reconcileDelayMs * 2, OAUTH_RECONCILE_MAX_DELAY_MS);
      const nextDelayMs =
        failedCallbackDeadlineMs === undefined
          ? reconcileDelayMs
          : Math.min(reconcileDelayMs, failedCallbackDeadlineMs - Date.now());
      reconcileTimerId = window.setTimeout(reconcileOnce, nextDelayMs);
    };
    reconcileTimerId = window.setTimeout(reconcileOnce, reconcileDelayMs);

    window.addEventListener('message', onMessage);
  });
}

/**
 * Poll Bower's own row for the authoritative outcome, carrying the gateway's
 * reason on a failure.
 *
 * The reason matters because the popup can't supply one. A failed OAuth does
 * NOT come back to `/connectors/callback` — the gateway keeps the user on its
 * own page and, if that page is refreshed, replaces the real cause with
 * "Invalid or expired state" (its authorisation state is single-use, so the
 * second delivery of the same callback always reports that regardless of what
 * actually went wrong). So the popup's `?status=` is the least informative
 * signal available.
 *
 * Our row has the real thing: `syncStatus` mirrors the gateway's
 * `statusReason` onto `lastErrorMessage` for every non-ACTIVE status
 * ("Authorization was denied at the provider (provider_error: access_denied)",
 * "…not completed within 10 minutes"). Surface that instead of a generic
 * retry prompt — the two are the difference between a user re-clicking
 * forever and a user knowing an admin has to approve the app.
 */
function reconcileFailureReason(connection: ConnectionSummary): string | undefined {
  const reason = connection.lastErrorMessage?.trim();
  return reason ? reason : undefined;
}

async function reconcileConnectorOAuth(
  workspaceId: string,
  connectionId: string,
  toolkitKey: string,
): Promise<{ outcome: OAuthOutcome; reason?: string } | null> {
  const response = await connectorsService.listConnections(workspaceId);
  if (!response.success || !response.data) return null;
  const connection = response.data.find(
    (candidate) => candidate.id === connectionId && candidate.toolkitKey === toolkitKey,
  );
  if (connection?.status === 'ACTIVE') return { outcome: 'success' };
  if (connection?.status === 'ERROR' || connection?.status === 'NEEDS_REAUTH') {
    return { outcome: 'failed', reason: reconcileFailureReason(connection) };
  }
  return null;
}

export function useToolkitCatalog() {
  const workspaceId = useWorkspaceId();
  return useQuery<ToolkitCatalogEntry[]>({
    queryKey: toolkitCatalogKey,
    queryFn: async () => {
      const res = await connectorsService.listToolkits();
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Failed to load connector catalog');
      }
      return res.data.items;
    },
    // Catalog is gateway-side static-ish; cache aggressively.
    staleTime: 30 * 60 * 1000,
    // The endpoint sits behind requireWorkspaceContext, which 400s without an
    // x-workspace-id header. A brand-new user mid-onboarding has no workspace
    // yet, so firing this unconditionally threw MISSING_WORKSPACE_CONTEXT (400)
    // on the onboarding page. Gate on workspace existence exactly like
    // useConnections — the connectors UI is only reachable inside a workspace.
    enabled: !!workspaceId,
  });
}

/** Bound on the failure-reason read — see `resolveConnectorFailure`. */
const FAILURE_REASON_DEADLINE_MS = 6_000;

/**
 * Resolve a failed full-page OAuth return: the gateway's reason for the
 * toast, AND the fresh rows so the caller can seed the cache.
 *
 * Both come from ONE request, deliberately issued straight at the service
 * rather than through the query cache. The full-page return mounts the tab,
 * so its initial connections fetch is usually still in flight — and every
 * cache route (`refetchQueries`, `invalidateQueries`) DEDUPES with it,
 * resolving with the pre-failure row and scheduling no second fetch. That is
 * why the caller must write the result back itself: an invalidate here is
 * not merely redundant, it is a no-op, which would leave the card reading
 * "Connected" under a toast saying the connection was denied.
 *
 * Deadline-bounded because the API client waits 30s and retries once on
 * timeout — up to ~61s. This is a failure path that used to toast instantly,
 * so a minute of silence is a worse regression than losing the reason. On
 * expiry the caller falls back to its generic copy.
 */
export async function resolveConnectorFailure(
  workspaceId: string | null | undefined,
  toolkitKey: string | null,
): Promise<{ reason?: string; rows?: ConnectionSummary[] }> {
  if (!workspaceId || !toolkitKey) return {};

  const read = (async (): Promise<{ reason?: string; rows?: ConnectionSummary[] }> => {
    try {
      const res = await connectorsService.listConnections(workspaceId);
      // The API client resolves `{ success: false }` on network failure
      // rather than throwing, so this — not the catch below — is the branch
      // a real outage takes.
      if (!res.success || !res.data) return {};
      const reason = res.data
        .find((row) => row.toolkitKey === toolkitKey)
        ?.lastErrorMessage?.trim();
      return { rows: res.data, reason: reason ? reason : undefined };
    } catch {
      // Defence in depth: the client is not expected to throw.
      return {};
    }
  })();

  return new Promise((resolve) => {
    const timerId = window.setTimeout(() => resolve({}), FAILURE_REASON_DEADLINE_MS);
    void read.then((value) => {
      window.clearTimeout(timerId);
      resolve(value);
    });
  });
}

export function useConnections(enabled = true) {
  const workspaceId = useWorkspaceId();
  return useQuery<ConnectionSummary[]>({
    queryKey: connectionsKey(workspaceId),
    queryFn: async () => {
      const res = await connectorsService.listConnections();
      if (!res.success || !res.data) {
        throw new Error(res.error?.message || 'Failed to load connections');
      }
      return res.data;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 60 * 1000,
  });
}

/**
 * Carries the backend's `error.code` alongside its message.
 *
 * The connector endpoints return distinct, already-user-facing codes
 * (`TOOLKIT_CAP_REACHED`, `CONNECTION_IN_PROGRESS`, `CONNECTOR_NOT_AVAILABLE`,
 * `CONNECTION_CHANGED`, `CONNECTOR_REVOKE_FAILED`). Collapsing them into one
 * generic toast tells a user whose provider is mid-setup, or who double-clicked
 * Connect, to "try again" — which is the one thing that cannot help and, for
 * the 409, is the retry the reservation exists to prevent.
 */
export class ConnectorRequestError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ConnectorRequestError';
  }
}

export interface StartConnectArgs {
  readonly toolkitKey: string;
  /**
   * The popup opened synchronously by the caller's click handler. Pass
   * `null` to force the full-page-redirect fallback.
   */
  readonly popup: Window | null;
  /** Full-page fallback destination. Popup flows still use the callback bridge. */
  readonly returnTo?: '/onboarding';
}

export function useStartConnect() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const toast = useAppToast();
  // The gateway's reason for a failure, captured during the reconcile loop so
  // `onSuccess` can show it. Held out-of-band rather than folded into the
  // mutation's resolved value because that value is the outcome union the
  // popup handshake is built around; widening it would ripple through every
  // caller for a field only the toast reads.
  //
  // KEYED BY TOOLKIT, and consumed on read. `ConnectorsTab` calls this hook
  // ONCE for the whole tab, so a single slot would be shared by every
  // connector card — and only the pending card's button is disabled, so a
  // user can start a second connector while the first is still inside its
  // 10-minute wait. With one slot, Microsoft's "admin approval required"
  // surfaced on a failing Slack card: specific, plausible, actionable and
  // completely wrong — worse than the generic string this replaces.
  //
  // Clearing at the start of an attempt does NOT fix that. With two attempts
  // in flight the second's reset runs before the first's reconcile writes,
  // so the carrier must be keyed to the attempt and emptied when read.
  const failureReasonsRef = useRef<Map<string, string>>(new Map());

  // Stop the reconcile loop when the owning component unmounts. One controller
  // covers every attempt started by this hook instance; each settled flow
  // removes its own listeners (including its abort listener — see finish()),
  // so a late abort is a no-op for them.
  //
  // StrictMode / Suspense-reveal trap: cleanup + effect re-run happen on the
  // SAME instance with refs preserved — the cleanup aborts the controller, and
  // a once-only ref would then hand every subsequent flow a pre-aborted signal
  // (instant 'cancelled': connector OAuth dead in dev, and after a Suspense
  // re-reveal in prod). Recreate the controller whenever the effect finds it
  // aborted. Lazy init so renders don't each allocate a discarded controller.
  const unmountAbortRef = useRef<AbortController | null>(null);
  const armedController = useCallback((): AbortController => {
    if (!unmountAbortRef.current || unmountAbortRef.current.signal.aborted) {
      unmountAbortRef.current = new AbortController();
    }
    return unmountAbortRef.current;
  }, []);
  useEffect(() => {
    const controller = armedController();
    return () => controller.abort();
  }, [armedController]);

  return useMutation({
    mutationFn: async ({
      toolkitKey,
      popup,
      returnTo,
    }: StartConnectArgs): Promise<OAuthOutcome | 'redirected'> => {
      // Drop any reason left by this toolkit's previous attempt, so a retry
      // that fails without one falls back to the generic string rather than
      // replaying the last failure's cause.
      failureReasonsRef.current.delete(toolkitKey);
      if (!workspaceId) {
        popup?.close();
        throw new ConnectorRequestError('A workspace is required to start a connection');
      }
      const usePopupBridge = !!popup && !popup.closed;
      const res =
        !usePopupBridge && returnTo
          ? await connectorsService.startOAuth(toolkitKey, returnTo)
          : await connectorsService.startOAuth(toolkitKey);
      if (!res.success || !res.data?.redirectUrl || !res.data.connectionId) {
        // Nothing to authorise against — close the placeholder popup so the
        // user isn't left staring at a blank window.
        popup?.close();
        throw new ConnectorRequestError(
          res.error?.message || 'Failed to start connection',
          res.error?.code,
        );
      }
      const { redirectUrl, connectionId } = res.data;

      safeAnalytics.track(ANALYTICS_EVENTS.CONNECTOR_CONNECT_STARTED, {
        toolkit_key: toolkitKey,
      });

      // Popup blocked (or none supplied): preserve the original full-page
      // redirect. The gateway sets cookies on its own domain during the
      // dance, so navigating the tab directly is the safe fallback. The
      // callback page bridges the return back to /settings via ?status=.
      if (!usePopupBridge) {
        window.location.href = redirectUrl;
        return 'redirected';
      }

      popup.location.href = redirectUrl;
      return waitForConnectorOAuth(
        popup,
        async () => {
          const settled = await reconcileConnectorOAuth(workspaceId, connectionId, toolkitKey);
          if (settled?.reason) failureReasonsRef.current.set(toolkitKey, settled.reason);
          return settled?.outcome ?? null;
        },
        armedController().signal,
      );
    },
    onSuccess: (outcome, variables) => {
      // Consume this attempt's reason — read once, then drop it, so it can
      // never be replayed onto a later failure or a different connector.
      const failureReason = failureReasonsRef.current.get(variables.toolkitKey);
      failureReasonsRef.current.delete(variables.toolkitKey);

      // Full-page fallback: the browser is already navigating away; the
      // callback page + ConnectorsTab's ?status= effect own the UX.
      if (outcome === 'redirected') return;

      if (outcome === 'success') {
        // The backend's atomic INITIATED → ACTIVE transition owns the single
        // canonical Connector Connected event. This client path owns UX only.
        toast({
          title: 'Connection complete',
          description: 'Your connection is being finalised…',
          status: 'success',
        });
      } else if (outcome === 'failed') {
        toast({
          title: 'Connection failed',
          // Prefer the gateway's own reason — see `reconcileConnectorOAuth`.
          // "Please try again" is actively misleading for the two commonest
          // causes (provider denied the grant; the app needs admin approval),
          // where retrying can never succeed.
          description:
            failureReason ?? 'The connector did not complete authorisation. Please try again.',
          status: 'error',
        });
      }
      // 'cancelled' (user closed the popup): no toast — but still refetch in
      // case auth actually completed and the close raced the message.
      void queryClient.invalidateQueries({ queryKey: connectionsKey(workspaceId) });
    },
    onError: (error) => {
      // `POST /start` failed (gateway down, tier gate, etc.). The popup has
      // already been closed in mutationFn, so without this the click looks
      // like a no-op (popup flashes and vanishes). Fail loud (rule 12) — and
      // with the backend's own copy, which is written for this user.
      toast({
        title: 'Couldn’t start the connection',
        description: connectorErrorDescription(
          error,
          'Something went wrong reaching the connector. Please try again.',
        ),
        status: 'error',
      });
    },
  });
}

/**
 * Prefer the backend's message: these endpoints return prose written for the
 * user ("Reached the Individual tier connector cap (10).", "…isn't available to
 * connect yet. We're finishing its setup."). Fall back only for errors that
 * never reached the API.
 */
function connectorErrorDescription(error: unknown, fallback: string): string {
  if (error instanceof ConnectorRequestError && error.message) return error.message;
  return fallback;
}

export function useDisconnectConnection() {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const toast = useAppToast();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await connectorsService.disconnect(id);
      if (!res.success) {
        throw new ConnectorRequestError(
          res.error?.message || 'Failed to disconnect',
          res.error?.code,
        );
      }
      return res.data;
    },
    onSuccess: () => {
      safeAnalytics.track(ANALYTICS_EVENTS.CONNECTOR_DISCONNECTED, {});
      queryClient.invalidateQueries({ queryKey: connectionsKey(workspaceId) });
    },
    onError: (error) => {
      // Disconnect revokes upstream BEFORE touching local state, and on failure
      // deliberately leaves the connection live so the UI cannot claim access
      // was removed. Without a toast the spinner just stops and the card still
      // reads "Connected" — indistinguishable from a dead click, on a
      // third-party data grant. Refetch so the row reflects reality.
      void queryClient.invalidateQueries({ queryKey: connectionsKey(workspaceId) });
      toast({
        title: 'Couldn’t disconnect',
        description: connectorErrorDescription(
          error,
          'Something went wrong reaching the connector. Your connection is still active — please try again.',
        ),
        status: 'error',
      });
    },
  });
}
